/**
 * Dragging a shared wall (PRP-SKETCH-FIELD-3 J17, H15; replaces FIELD-1 F6 and F5
 * rule 1 for dragging). The wall is one wall: its line moves by the same distance
 * in every room on it, so one room grows, the other shrinks, and they stay joined.
 * Openings on it keep their positions along the wall. Refused when the move would
 * change a measured size in any of those rooms, or take one below `room_min_ft`.
 * A fully missing wall (0 in) drags the same way. One undo step (`History.run`).
 */
import { POLICY } from './policy';
import { EPS, edgeOf, findRoom, outN, snapIn, tidy } from './geom';
import { buildUF, isMeasured } from './uf';
import { sharedMap } from './shared';
import { findOpposite } from './opposite';
import { moveLine, normalAxis } from './lineShift';
import { wallLines } from './heldMove';
import { wallFtOf } from './thickness';
import { jambStop, splitMissingAtJunctions } from './wallMeetsOpening';
import type { Fail, Plan } from './types';

export type SharedDragResult =
  | { ok: true; rooms: string[]; deltaFt: number; jamb?: string }
  | Fail<'missing' | 'not-shared' | 'no-move' | 'measured' | 'too-small'> & { rid?: string };

/**
 * Drag shared wall `vid` of room `rid` by `deltaFt` along its outward normal
 * (positive grows `rid`), snapped to the inch. Each room keeps the gap it had (the
 * wall keeps its thickness).
 */
export function dragSharedWall(plan: Plan, rid: string, vid: number, deltaFt: number): SharedDragResult {
  const r = findRoom(plan, rid);
  const E = r && edgeOf(r, vid);
  const ax = r ? normalAxis(r, vid) : null;
  if (!r || !E || !ax) return { ok: false, err: 'missing', message: 'That wall is gone' };
  if (!(sharedMap(plan).get(rid)?.get(vid)?.length)) {
    return { ok: false, err: 'not-shared', message: 'Pick a wall between two rooms' };
  }
  const d = snapIn(deltaFt) * outN(r, E.a, E.b)[ax];
  if (Math.abs(d) < EPS) return { ok: false, err: 'no-move', message: 'That wall did not move' };
  const lines = wallLines(plan, r, vid, ax);
  for (const l of lines) {
    const any = [...l.ids][0];
    if (isMeasured(l.r, buildUF(l.r), ax, any)) {
      return { ok: false, err: 'measured', rid: l.r.id, message: `${l.r.name}'s width is measured. Remeasure it to move this wall.` };
    }
  }
  // J17: refused at the point where any room would drop below room_min_ft.
  for (const l of lines) {
    for (const id of l.ids) {
      const W = edgeOf(l.r, id);
      if (!W || !l.ids.has(W.b.id)) continue;
      const opp = findOpposite(l.r, id);
      if (opp && opp.d + d * outN(l.r, W.a, W.b)[ax] < POLICY.room_min_ft - EPS) {
        return { ok: false, err: 'too-small', rid: l.r.id, message: `${l.r.name} would be under ${POLICY.room_min_ft} ft` };
      }
    }
  }
  for (const l of lines) moveLine(l.r, l.ids, ax, l.at + d);
  // FIELD-4 K7: the wall's T's never end inside a door or window (each moving room's own line checked).
  let jamb: string | undefined;
  for (const l of lines) {
    const stop = jambStop(plan, l.r.id, l.ids, ax, wallFtOf(r, vid));
    if (!stop) continue;
    for (const m of lines) moveLine(m.r, m.ids, ax, m.r.corners.find((c) => m.ids.has(c.id))![ax] + stop.shift);
    jamb = stop.message || undefined;
    break;
  }
  tidy(plan);
  splitMissingAtJunctions(plan);
  return { ok: true, rooms: [...new Set(lines.map((l) => l.r.id))], deltaFt: d, ...(jamb ? { jamb } : {}) };
}
